import axiosInstance, { getNewCancelToken } from '@/services/_AxiosInstance';

const urlBase = '/master/Rpm_Operation_Detail/';

/**
 * @typedef {Object} RpmOperationDetailPayload
 * @property {number} mrodMrohId - ID header operation RPM (`mrohId`).
 * @property {string} mrodName - Nama mode operasi RPM (contoh: `OFF`, `IDLE`).
 * @property {number} mrodRpmMax - Batas maksimum RPM untuk mode tersebut.
 * @property {number} mrodConsRate - Consumption rate mode tersebut.
 */

/**
 * @typedef {Object} RpmOperationDetailItem
 * @property {number} mrodId
 * @property {number} mrodMrohId
 * @property {string} mrodName
 * @property {number} mrodRpmMax
 * @property {string} mrodConsRate
 * @property {string} mrodCreatedUserId
 * @property {string} mrodCreatedTime
 * @property {string} mrodUpdatedUserId
 * @property {string} mrodUpdatedTime
 * @property {Object} [mrodMroh] - Data relasi header operation (`Rpm_Operation_Header`).
 */

/**
 * @typedef {Object} RpmOperationDetailListResponse
 * @property {Object<string, Object>} fields - Metadata field dari API.
 * @property {RpmOperationDetailItem[]} data - List detail operation.
 * @property {Object} pagination - Informasi pagination.
 * @property {Object} status - Status response API.
 */

/**
 * @typedef {Object} RpmOperationDetailCreateResponse
 * @property {number} id - ID record yang berhasil dibuat.
 * @property {Object} status - Status response API.
 */

/**
 * @typedef {Object} RpmOperationDetailMutationResponse
 * @property {number} affected_rows - Jumlah data yang terpengaruh.
 * @property {Object} status - Status response API.
 */

/**
 * Service untuk endpoint Master RPM Operation Detail.
 * @namespace RpmOperationDetailSvc
 */
export default {
  /**
   * Ambil list RPM Operation Detail.
   * @function getRpmOperationDetails
   * @param {Object} [params] - Query params mentah API.
   * Contoh: `{ page: 1, max: 20, finds: { mrodMrohId: 1 } }`.
   * @returns {Promise<RpmOperationDetailListResponse>}
   */
  getRpmOperationDetails: async (params = {}) => {
    const { data } = await axiosInstance.get(urlBase, {
      params,
      cancelToken: getNewCancelToken(),
    });
    return data;
  },

  /**
   * Ambil list RPM Operation Detail berdasarkan ID header operation.
   * @function getRpmOperationDetailsByHeaderId
   * @param {number} mrohId - ID header operation (`mrohId`).
   * @param {Object} [params] - Query tambahan lain (opsional), selain filter header.
   * @returns {Promise<RpmOperationDetailListResponse>}
   */
  getRpmOperationDetailsByHeaderId: async (mrohId, params = {}) => {
    console.log('Fetching RPM Operation Details with header ID:', mrohId, 'and params:', params);
    const newParams = { ...params };
    newParams['finds[mrodMrohId]'] = mrohId;
    const { data } = await axiosInstance.get(urlBase, {
      params: newParams,
      cancelToken: getNewCancelToken(),
    });
    return data;
  },

  /**
   * Buat data RPM Operation Detail baru.
   * @function createRpmOperationDetail
   * @param {RpmOperationDetailPayload} payload - Body request create.
   * @param {number} [id] - ID opsional pada path jika backend menggunakan format `.../{id}` saat create.
   * @returns {Promise<RpmOperationDetailCreateResponse>}
   */
  createRpmOperationDetail: async (payload, id) => {
    const endpoint = id ? `${urlBase}${id}` : urlBase;
    const { data } = await axiosInstance.post(endpoint, payload, {
      cancelToken: getNewCancelToken(),
    });
    return data;
  },

  /**
   * Update data RPM Operation Detail berdasarkan ID.
   * @function updateRpmOperationDetail
   * @param {number} id - ID data yang akan diupdate.
   * @param {RpmOperationDetailPayload} payload - Body request update.
   * @returns {Promise<RpmOperationDetailMutationResponse>}
   */
  updateRpmOperationDetail: async (id, payload) => {
    const { data } = await axiosInstance.put(`${urlBase}${id}`, payload, {
      cancelToken: getNewCancelToken(),
    });
    return data;
  },

  /**
   * Hapus data RPM Operation Detail berdasarkan ID.
   * @function deleteRpmOperationDetail
   * @param {number} id - ID data yang akan dihapus.
   * @returns {Promise<RpmOperationDetailMutationResponse>}
   */
  deleteRpmOperationDetail: async (id) => {
    const { data } = await axiosInstance.delete(`${urlBase}${id}`, {
      cancelToken: getNewCancelToken(),
    });
    return data;
  },
};
